import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Security } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { RequiredAuth } from './SecureRoute';
import LoginCallback from './LoginCallback';
import Main from './Main';
import Layout from './Layout';
import { oktaConfig } from '../config/oktaConfig';

// Create OktaAuth instance in Routes component per mfa-console pattern
const oktaAuth = new OktaAuth(oktaConfig);

const AppRoutes: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleSilentRefreshMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'okta:silent-refresh') return;
      if (typeof event.data.hash !== 'string' || event.data.hash.length === 0) return;

      try {
        const silentRefreshUrl = `${window.location.origin}/silent-refresh.html${event.data.hash}`;
        const { tokens } = await oktaAuth.token.parseFromUrl({ url: silentRefreshUrl });
        oktaAuth.tokenManager.setTokens(tokens);
        await oktaAuth.authStateManager.updateAuthState();
      } catch (_error) {
        // Best-effort bridge for hidden iframe callback; ignore malformed/expired responses.
      }
    };

    window.addEventListener('message', handleSilentRefreshMessage);
    return () => {
      window.removeEventListener('message', handleSilentRefreshMessage);
    };
  }, []);

  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri?: string) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  const customAuthHandler = async () => {
    await oktaAuth.signInWithRedirect();
  };

  return (
    <Security
      oktaAuth={oktaAuth}
      onAuthRequired={customAuthHandler}
      restoreOriginalUri={restoreOriginalUri}>
      <Layout>
        <Routes>
          <Route path='/' element={<RequiredAuth />}>
            <Route index element={<Main />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
          <Route path="/login/callback" element={<LoginCallback />} />
        </Routes>
      </Layout>
    </Security>
  );
};

export default AppRoutes;
